import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Button, Card, Flex, Modal, Typography } from 'antd';

import { useQueryClient } from '@tanstack/react-query';
import { useGetPlgAnticipation, usePlgSaveAccess } from 'v2/hooks/usePlgAnticipation';

type PlgAnticipationProps = {
  page: string;
  disabled?: boolean;
};

const PlgAnticipation = ({ page, disabled }: PlgAnticipationProps) => {
  const [open, setOpen] = useState(false);
  const [anticipateInProgress, setAnticipateInProgress] = useState(false);
  const [automaticAnticipateInProgress, setAutomaticAnticipateInProgress] = useState(false);

  const toggleAnticipateProgress = useCallback(() => setAnticipateInProgress(prev => !prev), []);
  const toggleAutomaticAnticipateProgress = useCallback(() => setAutomaticAnticipateInProgress(prev => !prev), []);
  const toggleVisibilityModal = useCallback(() => setOpen(prev => !prev), []);

  const history = useHistory();
  const queryClient = useQueryClient();
  const plgAnticipationQuery = useGetPlgAnticipation();
  const savePlgAccessMutation = usePlgSaveAccess({
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['plgAnticipation'] });
    },
    onError: () => {
      toggleAnticipateProgress();
      toggleAutomaticAnticipateProgress();
      toast.error('Ocorreu um erro! Tente novamente.');
    }
  });

  const someCallToActionInProgress = anticipateInProgress || automaticAnticipateInProgress;

  const saveAccess = async () => {
    await savePlgAccessMutation.mutateAsync(page);
    toggleVisibilityModal();
  };

  const handleClose = () => saveAccess();

  const handleAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=anticipation');
  };

  const handleAutomaticAnticipate = async () => {
    await saveAccess();
    history.push('/producer/financial-statement?a=automatic-anticipation');
  };

  useEffect(() => {
    if (!plgAnticipationQuery.data) return;
    const { show, screens } = plgAnticipationQuery.data;

    if (show && screens.includes(page)) {
      setOpen(true);
    }
  }, [page, plgAnticipationQuery.data]);

  return (
    <Modal
      open={open && !disabled}
      onCancel={handleClose}
      keyboard={false}
      title='Antecipação de recebíveis'
      footer={
        <div className='flex flex-wrap items-center justify-between gap-2'>
          <Button type='text' onClick={handleClose} disabled={someCallToActionInProgress}>
            Agora não
          </Button>
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button
              onClick={handleAutomaticAnticipate}
              disabled={someCallToActionInProgress}
              loading={automaticAnticipateInProgress}
            >
              Ativar antecipação automática
            </Button>
            <Button
              type='primary'
              onClick={handleAnticipate}
              disabled={someCallToActionInProgress}
              loading={anticipateInProgress}
            >
              Antecipar meus recebíveis
            </Button>
          </div>
        </div>
      }
    >
      <Flex vertical gap='large' tabIndex={-1}>
        <Flex align='center' gap='middle'>
          <Card size='small'>
            <img src='/img/rocket-take-off-icon.svg' alt='Rocket takeoff' className='fill-primary size-8' />
          </Card>
          <div>
            <Typography.Text strong>Antecipação de recebíveis</Typography.Text>
            <Typography.Paragraph type='secondary'>
              Acione o poder da antecipação de recebíveis em sua estratégia comercial. Agilize seu fluxo de caixa,
              conquiste liquidez imediata e potencialize seu crescimento no mercado digital. Vamos além juntos!
            </Typography.Paragraph>
          </div>
        </Flex>

        <Flex align='center' gap='middle'>
          <Card size='small'>
            <img src='/img/gift-icon.svg' alt='Gift' className='fill-primary size-8' />
          </Card>
          <div>
            <Typography.Text strong>Taxas reduzidas</Typography.Text>
            <Typography.Paragraph type='secondary'>
              Potencialize seus ganhos com nossas taxas reduzidas. Aumente sua rentabilidade e impulsione sua
              performance financeira.
            </Typography.Paragraph>
          </div>
        </Flex>

        <Flex align='center' gap='middle'>
          <Card size='small'>
            <img src='/img/cash-coin-icon.svg' alt='Cash coin' className='fill-primary size-8' />
          </Card>
          <div>
            <Typography.Text strong>Disponibilidade imediata de valores</Typography.Text>
            <Typography.Paragraph type='secondary'>
              Não espere para prosperar! Com nossa plataforma, tenha disponibilidade imediata de valores. Agilidade para
              impulsionar seus projetos e conquistar resultados rápidos no mundo do marketing digital.
            </Typography.Paragraph>
          </div>
        </Flex>
      </Flex>
    </Modal>
  );
};

export default PlgAnticipation;
